import { Fragment, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  Legend,
  Line,
  Area,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { OwnerFinanceSummary } from './components/OwnerFinanceSummary.js';
import LocationTargetsButton from './components/LocationTargetsButton.js';
import { AuthContext } from '../../context/AuthContext.js';
import dashboardService from '../../services/DashboardService.js';
import Alert from '../common/Alert.js';
import Loader from '../common/Loader.js';
import { DashboardLayout, PageHeader } from '../common/page_component/index.js';
import DateRangeFilter, { DEFAULT_DATE_RANGE } from './components/DateRangeFilter.js';
import DashboardPieChart from './components/DashboardPieChart.js';
import {
  CHART_DUE_COLOR,
  CHART_PAID_COLOR,
  CHART_SECONDARY_COLOR,
  CHART_NEUTRAL_COLOR,
  CHART_TOOLTIP_STYLE,
  formatChartValue,
} from './components/chartTheme.js';
import { fullCurrency } from '../../utils/formatCurrency.js';

const money = (value) => fullCurrency(value);

const greetingForHour = (hour) => {
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
};

const shortDate = (value) => {
  const [year, month, day] = String(value || '').split('-');
  if (!day) return value;
  const date = new Date(Number(year), Number(month) - 1, Number(day));
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
};

const shortLabel = (value, limit = 22) => {
  const text = String(value || '');
  return text.length > limit ? `${text.slice(0, limit - 1)}…` : text;
};

const OwnerDashboard = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [dateRange, setDateRange] = useState(DEFAULT_DATE_RANGE);
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [expandedHubId, setExpandedHubId] = useState(null);
  const [selectedAgentId, setSelectedAgentId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const params = { date_range: dateRange };
      if (dateRange === 'custom') {
        if (customStart) params.from_date = customStart;
        if (customEnd) params.to_date = customEnd;
      }
      const response = await dashboardService.getOwner(params);
      setData(response.data || null);
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to load owner dashboard');
    } finally {
      setLoading(false);
    }
  }, [dateRange, customStart, customEnd]);

  useEffect(() => {
    load();
  }, [load]);

  const split = data?.sales_split || {};
  const branches = data?.branches || [];
  const trend = data?.trend || [];
  const categories = data?.expense_categories || [];
  const firstName = (user?.name || 'Owner').split(' ')[0];
  const hour = new Date().getHours();
  const weakestDay = useMemo(
    () => trend.reduce((lowest, row) => (!lowest || row.net < lowest.net ? row : lowest), null),
    [trend],
  );
  const strongest = branches[0] || null;
  const weakest = branches.length > 1 ? branches[branches.length - 1] : null;

  const selectHub = (id) => {
    const next = Number(id);
    if (!next) return;
    setSelectedAgentId(null);
    setExpandedHubId((current) => (Number(current) === next ? null : next));
  };
  const selectAgent = (agentId) => {
    const next = String(agentId || '');
    if (!next) return;
    setSelectedAgentId((current) => (current === next ? null : next));
  };
  const expandedBranch = branches.find((row) => Number(row.location_hub_id) === Number(expandedHubId)) || null;
  const selectedAgent = (expandedBranch?.agents || []).find(
    (agent) => String(agent.agent_user_id) === String(selectedAgentId),
  ) || null;
  const profitRows = selectedAgent
    ? [
        { key: 'sales', label: 'Sales', amount: selectedAgent.sales, fill: CHART_PAID_COLOR },
        { key: 'commission', label: 'Commission', amount: selectedAgent.commission, fill: CHART_NEUTRAL_COLOR },
        { key: 'profit', label: 'Profit', amount: selectedAgent.net, fill: selectedAgent.net < 0 ? CHART_DUE_COLOR : CHART_SECONDARY_COLOR },
      ]
    : expandedBranch
      ? (expandedBranch.agents || []).map((agent) => ({
          key: agent.agent_user_id,
          label: shortLabel(agent.name),
          amount: agent.net,
          net: agent.net,
          rank: agent.rank,
        }))
      : branches.map((row) => ({
          key: row.location_hub_id,
          label: shortLabel(row.name),
          amount: row.net,
          net: row.net,
          rank: row.rank,
          location_hub_id: row.location_hub_id,
        }));

  const salesMix = [
    { key: 'paid', name: 'Paid', amount: split.paid },
    { key: 'to_pay', name: 'To Pay', amount: split.to_pay },
    { key: 'on_account', name: 'On Account', amount: split.on_account },
  ];

  return (
    <DashboardLayout>
      <PageHeader
        title="Owner Dashboard"
        subtitle={`${greetingForHour(hour)}, ${firstName}. Own-hub sales, commission, expenses, and branch rank.`}
      >
        <LocationTargetsButton />
        <DateRangeFilter
          wide
          value={dateRange}
          onChange={setDateRange}
          customStart={customStart}
          customEnd={customEnd}
          onCustomStartChange={setCustomStart}
          onCustomEndChange={setCustomEnd}
        />
      </PageHeader>

      {loading && !data ? (
        <Loader />
      ) : (
        <div className={`space-y-4 ${loading ? 'opacity-70' : ''}`}>
          <OwnerFinanceSummary data={data} />

          <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
              <div className="bg-white rounded-xl border border-gray-100 p-4">
                <h2 className="text-sm font-semibold text-gray-800 mb-1">Sales by payment</h2>
                <p className="text-xs text-gray-500 mb-2">Paid, to pay, and on account for own hubs in this range.</p>
                <DashboardPieChart rows={salesMix} emptyLabel="No sales in this range." />
              </div>
              <div className="bg-white rounded-xl border border-gray-100 p-4">
                <h2 className="text-sm font-semibold text-gray-800 mb-1">Expenses by category</h2>
                <p className="text-xs text-gray-500 mb-2">Approved own-hub spend, largest category first.</p>
                {categories.length === 0 ? (
                  <p className="text-sm text-gray-500 py-10 text-center">No own-hub expenses in this range.</p>
                ) : (
                  <div style={{ height: Math.max(220, Math.min(categories.length, 8) * 36) }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={categories.slice(0, 8).map((row) => ({ ...row, label: shortLabel(row.category, 18) }))}
                        layout="vertical"
                        margin={{ top: 4, right: 12, left: 8, bottom: 0 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" horizontal={false} />
                        <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={(value) => formatChartValue(value)} />
                        <YAxis type="category" dataKey="label" width={110} tick={{ fontSize: 11 }} />
                        <Tooltip formatter={(value) => money(value)} contentStyle={CHART_TOOLTIP_STYLE} />
                        <Bar dataKey="amount" name="Expense" fill={CHART_DUE_COLOR} radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>
              <div className="bg-white rounded-xl border border-gray-100 p-4">
                <h2 className="text-sm font-semibold text-gray-800">Expenses</h2>
                <p className="text-xs text-gray-500 mb-3">Open a box to see that list.</p>
                {[
                  {
                    title: 'Office',
                    rows: [
                      { label: 'With admin', key: 'with_admin', href: '/expenses?kind=office&queue=pending_admin', tone: 'bg-amber-50 border-amber-100' },
                      { label: 'With owner', key: 'with_owner', href: '/expenses?kind=office&queue=pending_owner', tone: 'bg-blue-50 border-blue-100' },
                      { label: 'Payment due', key: 'due', href: '/expenses?kind=office&status=approved', tone: 'bg-orange-50 border-orange-100' },
                      { label: 'Payment done', key: 'done', href: '/expenses?kind=office&status=paid', tone: 'bg-green-50 border-green-100' },
                      { label: 'Rejected', key: 'rejected', href: '/expenses?kind=office&status=rejected', tone: 'bg-gray-50 border-gray-100' },
                    ],
                    source: data?.expense_kinds?.office,
                  },
                  {
                    title: 'Own branch',
                    rows: [
                      { label: 'In review', key: 'in_review', href: '/expenses?kind=branch&queue=pending_accountant', tone: 'bg-amber-50 border-amber-100' },
                      { label: 'With admin', key: 'with_admin', href: '/expenses?kind=branch&queue=pending_admin', tone: 'bg-blue-50 border-blue-100' },
                      { label: 'Settled', key: 'settled', href: '/expenses?kind=branch&status=approved', tone: 'bg-green-50 border-green-100' },
                      { label: 'Rejected', key: 'rejected', href: '/expenses?kind=branch&status=rejected', tone: 'bg-gray-50 border-gray-100' },
                    ],
                    source: data?.expense_kinds?.branch,
                  },
                ].map((group) => (
                  <div key={group.title} className="mb-3 last:mb-0">
                    <p className="text-xs font-semibold text-gray-700 mb-1.5">{group.title}</p>
                    <div className="grid grid-cols-2 gap-1.5">
                      {group.rows.map((item, index) => {
                        const row = group.source?.[item.key] || { count: 0, amount: 0 };
                        const wide = group.rows.length % 2 === 1 && index === group.rows.length - 1;
                        return (
                          <button
                            key={item.key}
                            type="button"
                            onClick={() => navigate(item.href)}
                            className={`rounded-lg border px-2.5 py-1.5 text-left ${item.tone} hover:shadow-sm ${wide ? 'col-span-2' : ''}`}
                          >
                            <span className="block text-[11px] font-medium text-gray-500 leading-tight">{item.label}</span>
                            <span className="block text-sm font-bold text-gray-900 leading-tight">{Number(row.count || 0)}</span>
                            <span className="block text-[11px] font-semibold text-gray-800 tabular-nums">{money(row.amount)}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-100 p-4">
            <div className="flex flex-wrap items-end justify-between gap-2 mb-3">
              <div>
                <h2 className="text-sm font-semibold text-gray-800">Daily trend</h2>
                <p className="text-xs text-gray-500">Sales against the net left after commission and expense. A dip in the blue area is a weak day.</p>
              </div>
              {weakestDay && (
                <p className={`text-xs font-medium ${weakestDay.net < 0 ? 'text-red-600' : 'text-gray-600'}`}>
                  Softest day {shortDate(weakestDay.date)} · net {money(weakestDay.net)}
                </p>
              )}
            </div>
            {trend.length === 0 ? (
              <p className="text-sm text-gray-500">No daily movement in this range.</p>
            ) : (
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={trend} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={shortDate} minTickGap={24} />
                    <YAxis tick={{ fontSize: 11 }} tickFormatter={(value) => formatChartValue(value)} width={72} />
                    <Tooltip
                      formatter={(value) => money(value)}
                      labelFormatter={shortDate}
                      contentStyle={CHART_TOOLTIP_STYLE}
                    />
                    <Legend />
                    <ReferenceLine y={0} stroke="#9ca3af" />
                    <Area type="monotone" dataKey="net" name="Net" stroke={CHART_SECONDARY_COLOR} fill="#dbeafe" strokeWidth={2} />
                    <Line type="monotone" dataKey="sales" name="Sales" stroke={CHART_PAID_COLOR} dot={false} strokeWidth={2} />
                    <Line type="monotone" dataKey="commission" name="Commission" stroke={CHART_NEUTRAL_COLOR} dot={false} strokeWidth={2} />
                    <Line type="monotone" dataKey="expense" name="Expense" stroke={CHART_DUE_COLOR} dot={false} strokeWidth={2} />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-5 gap-4">
            <div className="xl:col-span-3 bg-white rounded-xl border border-gray-100 p-4">
              <h2 className="text-sm font-semibold text-gray-800">
                {selectedAgent
                  ? selectedAgent.name
                  : expandedBranch
                    ? `Agents in ${shortLabel(expandedBranch.name, 32)}`
                    : 'Profit by branch'}
              </h2>
              <p className="text-xs text-gray-500 mb-3">
                {selectedAgent
                  ? 'Sales, commission, and profit for this agent. Click the name again to see every agent on the branch.'
                  : expandedBranch
                    ? 'Click an agent name to open that agent on this chart. Click the branch again to return to every branch.'
                    : 'Sales left after commission and expense. Click a bar to see that branch’s agents. Green is the strongest, red is a loss.'}
              </p>
              {branches.length === 0 ? (
                <p className="text-sm text-gray-500">No own location hubs yet.</p>
              ) : profitRows.length === 0 ? (
                <p className="text-sm text-gray-500">No agents on this branch.</p>
              ) : (
                <div className="max-h-[420px] overflow-y-auto pr-1">
                <div style={{ height: Math.max(280, profitRows.length * 42) }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={profitRows}
                      layout="vertical"
                      margin={{ top: 4, right: 16, left: 8, bottom: 0 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" horizontal={false} />
                      <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={(value) => formatChartValue(value)} />
                      <YAxis type="category" dataKey="label" width={140} tick={{ fontSize: 11 }} />
                      <Tooltip formatter={(value, name) => [money(value), name]} contentStyle={CHART_TOOLTIP_STYLE} />
                      <ReferenceLine x={0} stroke="#9ca3af" />
                      <Bar
                        dataKey="amount"
                        name="Amount"
                        cursor={selectedAgent ? 'default' : 'pointer'}
                        radius={[0, 4, 4, 0]}
                        onClick={(bar) => {
                          const payload = bar?.payload || bar;
                          if (!expandedBranch) {
                            selectHub(payload?.location_hub_id);
                            return;
                          }
                          if (!selectedAgent && payload?.key) selectAgent(payload.key);
                        }}
                      >
                        {profitRows.map((row) => {
                          let fill = row.fill || '#93c5fd';
                          if (!row.fill) {
                            if (row.net < 0) fill = CHART_DUE_COLOR;
                            else if (row.rank === 1) fill = CHART_PAID_COLOR;
                          }
                          return <Cell key={row.key} fill={fill} />;
                        })}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                </div>
              )}
            </div>

            <div className="xl:col-span-2 bg-white rounded-xl border border-gray-100 p-4 overflow-hidden">
              <h2 className="text-sm font-semibold text-gray-800">Branch ranking</h2>
              <p className="text-xs text-gray-500 mb-3">Branches ordered by profit. Commission and expense are each a share of that branch’s sales.</p>
              {branches.length === 0 ? (
                <p className="text-sm text-gray-500">No own location hubs yet.</p>
              ) : (
                <div className="max-h-[420px] overflow-auto">
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="text-left text-gray-500 border-b border-gray-100 sticky top-0 bg-white">
                        <th className="py-2 pr-2 font-medium">#</th>
                        <th className="py-2 pr-2 font-medium">Branch</th>
                        <th className="py-2 pr-2 font-medium">Net</th>
                        <th className="py-2 pr-2 font-medium">Comm.</th>
                        <th className="py-2 font-medium">Exp.</th>
                      </tr>
                    </thead>
                    <tbody>
                      {branches.map((row) => {
                        const active = Number(expandedHubId) === Number(row.location_hub_id);
                        const isStrong = strongest && row.location_hub_id === strongest.location_hub_id;
                        const isWeak = weakest && row.location_hub_id === weakest.location_hub_id;
                        const tone = active
                          ? 'bg-blue-50'
                          : isStrong
                            ? 'bg-green-50'
                            : isWeak
                              ? 'bg-red-50'
                              : '';
                        return (
                          <Fragment key={row.location_hub_id}>
                          <tr
                            className={`border-b border-gray-50 cursor-pointer ${tone}`}
                            onClick={() => selectHub(row.location_hub_id)}
                          >
                            <td className="py-2 pr-2 font-semibold">{row.rank}</td>
                            <td className="py-2 pr-2">
                              <p className="font-medium text-gray-800">{shortLabel(row.name, 28)}</p>
                              <p className="text-[11px] text-gray-500">
                                {isStrong ? 'Strongest · ' : ''}
                                {isWeak ? 'Weakest · ' : ''}
                                {money(row.sales)} sales
                                {active ? ` · ${(row.agents || []).length} agents` : ''}
                              </p>
                            </td>
                            <td className={`py-2 pr-2 font-semibold ${row.net < 0 ? 'text-red-600' : 'text-green-700'}`}>{money(row.net)}</td>
                            <td className="py-2 pr-2">{Number(row.commission_rate || 0).toFixed(1)}%</td>
                            <td className="py-2">{Number(row.expense_rate || 0).toFixed(1)}%</td>
                          </tr>
                          {active && (row.agents || []).map((agent) => {
                            const picked = String(selectedAgentId) === String(agent.agent_user_id);
                            return (
                            <tr
                              key={agent.agent_user_id}
                              className={`border-b border-gray-50 cursor-pointer ${picked ? 'bg-blue-50' : 'bg-white'}`}
                              onClick={() => selectAgent(agent.agent_user_id)}
                            >
                              <td className="py-1.5 pr-2 text-xs text-gray-500">{agent.rank}</td>
                              <td className="py-1.5 pr-2 pl-3">
                                <button
                                  type="button"
                                  className={`text-sm text-left hover:underline ${picked ? 'font-semibold text-blue-700' : 'text-gray-800'}`}
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    selectAgent(agent.agent_user_id);
                                  }}
                                >
                                  {shortLabel(agent.name, 26)}
                                </button>
                                <p className="text-[11px] text-gray-500">{money(agent.sales)} sales</p>
                              </td>
                              <td className={`py-1.5 pr-2 text-sm font-medium ${agent.net < 0 ? 'text-red-600' : 'text-green-700'}`}>{money(agent.net)}</td>
                              <td className="py-1.5 pr-2 text-sm">{Number(agent.commission_rate || 0).toFixed(1)}%</td>
                              <td className="py-1.5 text-sm text-gray-400">—</td>
                            </tr>
                            );
                          })}
                          {active && (row.agents || []).length === 0 && (
                            <tr className="border-b border-gray-50">
                              <td colSpan={5} className="py-2 pl-3 text-xs text-gray-500">No agents on this branch.</td>
                            </tr>
                          )}
                          </Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default OwnerDashboard;
